import _ from 'lodash';
import { parseSummaryToTypeAndValue } from '@/annotation/parseSummaryToTypeAndValue';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { REPORT_CONTENT, SCREENSHOT_SIZE_TO_CONTENT } from '@/reportEditor/report.constants';
import { getReportContentShapeConstant, getReportContentSizeConstant } from '@/reportEditor/reportContent.store';
import { formatAsQueryString } from '@/utilities/httpHelpers.utilities';
import type { AssetSelection, Content, DateRange, InProgressContentProperties } from '@/reportEditor/report.constants';
import type { ReportContentState } from '@/reportEditor/reportContent.store';

/** The in-progress content fields the preview URL depends on. */
export type ReportContentPreviewFields = Pick<
  ReportContentState,
  | 'worksheetId'
  | 'workstepId'
  | 'height'
  | 'width'
  | 'scale'
  | 'dateRangeId'
  | 'assetSelectionId'
  | 'useSizeFromRender'
  | 'hideUncertainty'
  | 'analysisViewType'
  | 'summary'
>;

/** The in-progress content fields compared with the saved content to tell whether it was modified. */
export type ReportContentModifiedFields = Pick<
  ReportContentState,
  | 'id'
  | 'workstepId'
  | 'height'
  | 'width'
  | 'scale'
  | 'useSizeFromRender'
  | 'dateRangeId'
  | 'summary'
  | 'assetSelectionId'
  | 'isReact'
  | 'hideUncertainty'
  | 'analysisViewType'
>;

/** The in-progress content fields a properties snapshot copies, which is all of them. */
export type ReportContentPropertiesFields = Pick<
  ReportContentState,
  | 'id'
  | 'modalName'
  | 'dateRangeId'
  | 'assetSelectionId'
  | 'width'
  | 'height'
  | 'sizeKey'
  | 'shapeKey'
  | 'scale'
  | 'useSizeFromRender'
  | 'canUseReact'
  | 'isReact'
  | 'analysisViewType'
  | 'summary'
  | 'fillToFit'
  | 'hideUncertainty'
  | 'workbookId'
  | 'workbookType'
  | 'worksheetId'
  | 'workstepId'
  | 'sourceUrl'
>;

/**
 * Builds the URL of the screenshot preview for the in-progress content.
 *
 * @param content - the in-progress content fields the preview depends on
 * @param report - report data the content references
 * @param report.reportId - ID of the report the content belongs to
 * @param report.timezoneName - name of the worksheet's timezone
 * @param report.assetSelections - the report's non-archived asset selections
 */
export function buildReportContentPreviewUrl(
  {
    worksheetId,
    workstepId,
    height,
    width,
    scale,
    dateRangeId,
    assetSelectionId,
    useSizeFromRender,
    hideUncertainty,
    analysisViewType,
    summary,
  }: ReportContentPreviewFields,
  {
    reportId,
    timezoneName,
    assetSelections,
  }: { reportId: string | undefined; timezoneName: string | undefined; assetSelections: AssetSelection[] },
): string {
  const assetId = assetSelections.find((selection) => selection.id === assetSelectionId)?.asset.id;
  const params = {
    worksheetId,
    workstepId,
    height,
    width,
    scale,
    dateRangeId,
    assetId,
    timezone: timezoneName,
    selector: useSizeFromRender ? SCREENSHOT_SIZE_TO_CONTENT.SELECTOR : undefined,
    reportId,
    hideUncertainty: hideUncertainty === true ? true : undefined,
    showUncertainty: hideUncertainty === false ? true : undefined,
    analysisViewType: analysisViewType ?? undefined,
    ...parseSummaryToTypeAndValue(summary),
  };

  return `${APPSERVER_API_PREFIX}/content/preview?${formatAsQueryString(params)}`;
}

/**
 * True when the in-progress content is new or differs from its saved version.
 * Exported only for test purposes.
 *
 * @param content - the in-progress content fields to compare
 * @param savedContent - the saved version of the content from the report, if any
 */
export function areReportContentParamsModified(
  {
    id,
    workstepId,
    height,
    width,
    scale,
    useSizeFromRender,
    dateRangeId,
    summary,
    assetSelectionId,
    isReact,
    hideUncertainty,
    analysisViewType,
  }: ReportContentModifiedFields,
  savedContent: Content | undefined,
): boolean {
  if (!id || !savedContent) return true;
  return (
    savedContent.workstepId !== workstepId ||
    (!useSizeFromRender && (savedContent.height !== height || savedContent.width !== width)) ||
    savedContent.scale !== scale ||
    savedContent.useSizeFromRender !== useSizeFromRender ||
    savedContent.dateRangeId !== dateRangeId ||
    !_.isEqual(savedContent.summary, summary) ||
    savedContent.assetSelectionId !== assetSelectionId ||
    savedContent.isReact !== isReact ||
    savedContent.hideUncertaintyOverride !== hideUncertainty ||
    savedContent.analysisViewType !== analysisViewType
  );
}

/**
 * Snapshots the in-progress content into the shape the Report Content modal edits and saves.
 *
 * @param content - every in-progress content field
 * @param report - report data the content references
 * @param report.dateRanges - all of the report's date ranges
 * @param report.savedContent - the saved version of the content from the report, if any
 */
export function buildReportContentProperties(
  content: ReportContentPropertiesFields,
  { dateRanges, savedContent }: { dateRanges: DateRange[]; savedContent: Content | undefined },
): InProgressContentProperties {
  const {
    id,
    modalName,
    dateRangeId,
    assetSelectionId,
    width,
    height,
    sizeKey,
    shapeKey,
    scale,
    useSizeFromRender,
    canUseReact,
    isReact,
    analysisViewType,
    summary,
    fillToFit,
    hideUncertainty,
    workbookId,
    workbookType,
    worksheetId,
    workstepId,
    sourceUrl,
  } = content;
  const dateRange = dateRanges.find((range) => range.id === dateRangeId);
  return {
    id: id ?? null,
    sourceUrl: sourceUrl ?? '',
    autoUpdate: Boolean(dateRange?.auto?.enabled),
    modalName: modalName ?? '',
    paramsModified: areReportContentParamsModified(content, savedContent),
    isReact: isReact ?? false,
    fillToFit: fillToFit ?? false,
    hideUncertaintyOverride: hideUncertainty ?? null,
    canUseReact: canUseReact ?? false,
    useSizeFromRender: useSizeFromRender ?? false,
    assetSelectionId: assetSelectionId?.toString() ?? null,
    dateRangeId: dateRangeId ?? null,
    shapeConstant: getReportContentShapeConstant(shapeKey),
    shapeKey,
    sizeConstant: getReportContentSizeConstant(sizeKey),
    sizeKey,
    summary: summary ?? REPORT_CONTENT.SUMMARY.NONE,
    scale,
    height,
    width,
    // In-progress content never carries names; the modal looks them up from the workbook when it needs them.
    worksheetName: '',
    worksheetId: worksheetId!,
    workbookName: '',
    workbookId: workbookId!,
    workbookType: workbookType ?? '',
    workstepId: workstepId!,
    isNew: !id,
    name: `content_${worksheetId ?? ''}_${workstepId ?? ''}`,
    analysisViewType,
  };
}
